// Muss vor allem anderen geladen werden: fängt das früh feuernde
// beforeinstallprompt-Event für den App-Install-Banner ab.
import './utils/installPrompt';
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './index.css';
import App from './App';

// -----------------------------------------------------------------------------
// Defensiver Schutz gegen DOM-Mutationen durch Browser-Übersetzungstools
// (Google Translate / Chrome "Auto-Translate", Edge Translator, u.ä.)
// -----------------------------------------------------------------------------
// Diese Tools durchsuchen den gerenderten DOM-Baum nach Textknoten und ersetzen
// sie eigenmächtig durch <font style="vertical-align: inherit;">…</font>-Wrapper,
// OHNE React davon in Kenntnis zu setzen (React kennt nur seinen eigenen
// virtuellen DOM-Baum). Wenn React danach beim nächsten Commit denselben
// Textknoten per removeChild/insertBefore verschieben oder entfernen will, findet
// es ihn an der erwarteten Stelle im echten DOM nicht mehr (er wurde durch das
// <font>-Element ersetzt) und wirft:
//   "NotFoundError: Failed to execute 'removeChild'/'insertBefore' on 'Node':
//    The node to be removed/before is not a child of this node."
// Diese Exception fällt in React 18 nicht auf ein einzelnes Feature zurück,
// sondern zerstört den kompletten Commit-Zyklus und wird von der obersten
// ErrorBoundary aufgefangen — die gesamte App "stürzt ab" (weißer Bildschirm /
// Fehlerseite), obwohl der eigentliche Fachprozess (z.B. Konto-Löschung) bereits
// erfolgreich abgeschlossen war. Das ist ein seit Jahren bekanntes React-Problem
// (siehe facebook/react#11538), das React selbst nicht lösen kann, weil es aus
// Performance-Gründen keine DOM-Fremdmutationen überwacht.
//
// Fix: Node.prototype.removeChild und Node.prototype.insertBefore werden hier
// gepatcht, um GENAU diese eine Fehlerkonstellation defensiv abzufangen:
//   - removeChild: Ist das zu entfernende Kind gar kein Kind mehr von "this"
//     (weil es durch Google Translate ersetzt wurde), wird NICHT geworfen,
//     sondern das übergebene Kind unverändert zurückgegeben (entspricht dem
//     Vertrag von removeChild, ohne den bereits "verschwundenen" Knoten aus dem
//     DOM entfernen zu müssen).
//   - insertBefore: Ist der Referenzknoten kein Kind mehr von "this", wird auf
//     appendChild ausgewichen (Best-Effort: Element wird trotzdem eingefügt,
//     nur ggf. an anderer Position als ursprünglich vorgesehen).
//
// WICHTIG (Eingrenzung auf nachweislich übersetzten DOM): Eine bloße
// parentNode-Inkonsistenz kann auch durch echte Reconciler-/Hydration-Bugs
// entstehen (z.B. Hydration-Mismatch bei serverseitig vorgerenderten Seiten,
// DOM-Fremdmanipulation durch react-leaflet oder signature-canvas). Würde der
// Fallback in JEDEM solchen Fall greifen, würden echte Bugs lautlos maskiert
// statt geworfen. Der Fallback darf daher NUR aktiv werden, wenn zusätzlich
// isDomTranslated() bestätigt, dass der Browser den DOM tatsächlich aktiv
// übersetzt (Google-Translate-Marker-Klasse am <html>-Element ODER die
// charakteristischen <font style="vertical-align: inherit">-Wrapper sind im
// DOM nachweisbar). Liegt KEINE Übersetzung vor, wird unverändert die native
// Implementierung aufgerufen — inklusive aller Original-Exceptions. Es wird
// also kein Fehlerzustand verschluckt, der nicht exakt dieser bekannten
// Drittanbieter-DOM-Mutation entspricht.
//
// Das Eingreifen dieses Patches wird bewusst UNCONDITIONAL geloggt (auch in
// Production, nicht nur in development): Es gibt kein Monitoring-Backend, und
// der appendChild-Fallback in insertBefore kann eine stille, für den Nutzer
// unsichtbare DOM-Reihenfolgeabweichung erzeugen. Ohne Log in Production wäre
// jedes Eingreifen des Patches nicht diagnostizierbar (analog zur Begründung
// für console.error in ErrorBoundary.componentDidCatch).

/**
 * Erkennt, ob der Browser den DOM aktiv übersetzt (Google Translate / Chrome
 * "Auto-Translate" o.ä.). Wird bewusst NUR bei Bedarf (beim tatsächlichen
 * Patch-Eingriff) ausgewertet, kein Dauerpolling/MutationObserver.
 *
 * Erkennungsmerkmale:
 *  - Chrome/Google Translate setzt "translated-ltr"/"translated-rtl" als
 *    CSS-Klasse am <html>-Element, sobald eine Übersetzung aktiv ist.
 *  - Fallback-Signatur für andere Übersetzer, die diese Klassen nicht setzen:
 *    Vorhandensein der charakteristischen <font style="vertical-align:
 *    inherit">-Wrapper, mit denen Google Translate Textknoten ersetzt.
 */
function isDomTranslated() {
  const htmlClassList = document.documentElement.classList;
  if (htmlClassList.contains('translated-ltr') || htmlClassList.contains('translated-rtl')) {
    return true;
  }
  return document.querySelector('font[style*="vertical-align: inherit"]') !== null;
}

const originalRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function patchedRemoveChild(child) {
  if (child && child.parentNode !== this && isDomTranslated()) {
    // eslint-disable-next-line no-console
    console.warn(
      '[dom-patch] removeChild: Kind ist kein Kind dieses Knotens mehr ' +
        '(Browser-Übersetzung aktiv erkannt) — Aufruf wird ignoriert.'
    );
    return child;
  }
  return originalRemoveChild.call(this, child);
};

const originalInsertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function patchedInsertBefore(newNode, referenceNode) {
  if (referenceNode && referenceNode.parentNode !== this && isDomTranslated()) {
    // eslint-disable-next-line no-console
    console.warn(
      '[dom-patch] insertBefore: Referenzknoten ist kein Kind dieses Knotens ' +
        'mehr (Browser-Übersetzung aktiv erkannt) — Fallback auf appendChild.'
    );
    return this.appendChild(newNode);
  }
  return originalInsertBefore.call(this, newNode, referenceNode);
};

const container = document.getElementById('root');
const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}
